<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useClaimStore, useContextStore, useNotificationStore } from '@/store';

import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { Bars3Icon, ChevronDownIcon } from '@heroicons/vue/20/solid'
import HeroIcons from '@/components/ui/icons/HeroIcons.vue'
import AlertModal from '@/components/ui/modals/AlertModal.vue'
import UpdateClaimModal from "../components/modals/UpdateClaimModal.vue";

import { copyToClipboard, openBoxFolderForCurrentClaim } from "@/utils";
import openTribunalDigitalForCurrentClaim from '@/utils/open-tribunal-digital-procedure';
import { ClaimLegalStepName } from "@/utils/config/constants/claims-legal-steps";
import { HeroIconNames } from '@/interfaces';
import SendEventModal from './ui/modals/SendEventModal.vue';
import type { AdminRole } from '@upclaim/sdk/src/extra-types';
import adminAPI from '@/services/api';
import CreateClaimTask from './Task/CreateClaimTask.vue';
import downloadFileFromResponse from '@/services/api/download-file-from-response';

interface Navigation {
  name: string;
  routeName: string;
  permissions: Array<AdminRole> | "*";
  current: boolean;
  dot?: boolean;
}

const navigation = computed<Navigation[]>(() =>[
  { name: 'Dossier', permissions: "*", routeName: 'ClaimOverview', current: true },
  { name: 'Timeline', permissions: "*", routeName: 'ClaimTimeline', current: false },
  { name: 'Feedback', permissions: "*", routeName: 'AcquisitionHistory', current: false,
    dot: useClaimStore().claim.claimLegalStep.name === "CLAIM_VALIDATION",
  },
  { name: 'Réservation', permissions: "*", routeName: 'ClaimPassengers', current: false },
]);

interface Action {
  name: string;
  heroIcon: HeroIconNames;
  legalSteps?: Array<ClaimLegalStepName>;
  disabledSteps?: Array<ClaimLegalStepName>;
  permissions?: Array<string>;
  click?: any;
}

const machineStateActions = computed<Action[]>(() => {
  return useClaimStore()?.machineState?.nextEvents.map(event => ({
    name: event,
    heroIcon: "ArrowRightIcon",
    click: () => {
      sendEvent.value = {
        open: true,
        event,
      }
    }
  })) || []
});

const sendEvent = ref<{ open: boolean; event: null | string }>({
  open: false,
  event: null
})



const staticActions: Array<Action> = [
  {
    name: "Voir les documents",
    permissions: ["UPCLAIM_ADMIN", "LAWYER", "EXTERNAL_LAWYER"],
    heroIcon: "FolderOpenIcon",
    click: () => openBoxFolderForCurrentClaim()
  },
  {
    name: "Créer une Tâche", heroIcon: "BellAlertIcon",
    click: () => { modalCreateTask.value = true },
    permissions: ["UPCLAIM_ADMIN", "LAWYER"],
  },
  {
    name: "Modifier le dossier", heroIcon: "PencilSquareIcon",
    click: () => { updateClaimModal.value = true },
    permissions: ["UPCLAIM_ADMIN", "LAWYER"],
  },
  {
    name: "Voir sur le Tribunal Digital", heroIcon: "ScaleIcon",
    click: () => openTribunalDigitalForCurrentClaim(),
    legalSteps: ["BAILIFF", "COURT", "WAITING_FOR_MONEY", "LOST"],
    permissions: ["UPCLAIM_ADMIN", "LAWYER"],
  },
  {
    name: "M'abonner aux notifications", heroIcon: "BellAlertIcon",
    click: () => openTribunalDigitalForCurrentClaim(),
  },
  {
    name: "Ne plus recevoir d'alertes", heroIcon: "BellSlashIcon",
    click: () => openTribunalDigitalForCurrentClaim(),
  },
  {
    name: "Copier l'ID de la claim", heroIcon: "ClipboardDocumentIcon",
    click: () => { copyToClipboard(useClaimStore().claim.id) },
    permissions: ["UPCLAIM_ADMIN", "LAWYER"],
  },
  {
    name: "Copier le numéro de PNR", heroIcon: "ClipboardDocumentIcon",
    click: () => { copyToClipboard(useClaimStore().claim.legalSpecification.impactedSegment.reservation.gdsPnrId) },
    permissions: ["CUSTOMER_AGENT", "CUSTOMER_ADMIN", "BUSINESS_TRAVEL_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"],
  },
  {
    name: "Réinitialiser la timeline",
    legalSteps: ["CLAIM_VALIDATION", "CANCELLED"],
    permissions: ["UPCLAIM_ADMIN"],
    heroIcon: "ArrowPathIcon",
    click: async () => {
      const claimId = useClaimStore().claim?.id;
      await adminAPI.resetClaimTimeline(claimId);
      useClaimStore().retrieveClaim(claimId);
      useNotificationStore().emitNotification({
        type: "success",
        title: "Timeline réinitialisée avec succès"
      });
    }
  },
  {
    name: "Synchroniser les tickets",
    // legalSteps: ["CLAIM_VALIDATION", "CANCELLED"],
    permissions: ["UPCLAIM_ADMIN"],
    heroIcon: "TicketIcon",
    click: async () => {
      const { reservationId } = useClaimStore().claim?.legalSpecification.impactedSegment;
      const waitNotif = useNotificationStore().emitNotification({
          type: "wait",
          title: "Synchronisation en cours..."
        });
      try {
        await adminAPI.syncReservationTickets(reservationId);
        useClaimStore().retrieveClaim(useClaimStore().claim.id);
        useNotificationStore().emitNotification({
          type: "success",
          title: "Billets synchronisés avec succès"
        });
      } catch (error) {
        useNotificationStore().emitNotification({
          type: "info",
          title: "Impossible de synchroniser l'historique des tickets"
        });
      }  finally {
        waitNotif.duration = 0;
      }
    }
  },
  // {
  //   name: 'Claim précédente', heroIcon: "ArrowNarrowLeftIcon",
  // },
  // {
  //   name: 'Synchroniser RPVA', heroIcon: "ScaleIcon",
  //   legalSteps: [ "COURT", "BAILIFF" ]
  // },
]

const stateActions: Array<Action> = [
  {
    name: "Générer les cessions de créance",
    legalSteps: ["CLAIM_VALIDATION"],
    permissions: ["*"],
    heroIcon: "DocumentDuplicateIcon",
    click: async () => {
      const loadingNotification = useNotificationStore().emitNotification({
        type: "wait",
        title: "Document en cours de génération (~30 sec à 1min)...",
        
      }, 90000);
      const claimId = useClaimStore().claim?.id;
      const documentType = "receivable-contract";
      const response = await adminAPI.instance.post(
        `/admin/claims/${claimId}/documents/${documentType}`,
        null,
        { responseType: "blob" }
      );
      loadingNotification.duration = 0;
      downloadFileFromResponse(response);
    }
  },
]

if (!useContextStore().isPlatformBrowser) {
  const adminLink = useContextStore().adminLink;
  const secondaireTag = useContextStore().frontContext.conversation.tags.find(l => l.name === "Secondaire"); // @TODO From config

  const tags = await useContextStore().frontContext?.listTags() // @TODO Store in ContextStore ?
  const orphelineTag = tags.results.find(l => l.name === "Orpheline"); // @TODO From config

  staticActions.push({
    name: 'Voir les conversations associées', heroIcon: "MagnifyingGlassIcon",
    click: () => useContextStore().frontContext?.search("link:"+useContextStore().adminLink?.id),
  });

  if (adminLink && secondaireTag && orphelineTag) {
    staticActions.push({
      name: 'Dissocier cette conversation', heroIcon: "XMarkIcon",
      click: async () => {
        await useContextStore().frontContext.removeLink(adminLink.id)
        await useContextStore().frontContext.tag([orphelineTag.id])
        await useContextStore().frontContext.untag([secondaireTag.id])
        useClaimStore().retrieveClaim(useContextStore().frontContext.conversation.id)
        useNotificationStore().emitNotification({
          title: "Opération effectuée avec succès",
          type: "success",
        })
      },
    })
  }
} else {
  staticActions.push({
    name: 'Ouvrir dans Front', heroIcon: "ArrowTopRightOnSquareIcon", 
    permissions: ["UPCLAIM_ADMIN", "LAWYER"],
    click: () => {
      const claim = useClaimStore().claim;
      const conversationId = claim.conversation?.frontConversationId
      if (!conversationId) {
        return useNotificationStore().emitNotification({
          title: "Cette claim n'est pas encore validée",
          type: "info"
        });
      }
      window.open(`https://app.frontapp.com/open/${conversationId}`, "_blank")?.focus();
    }
  });
}

const actions = computed(() => {
  return staticActions.filter(a => {
    const claimStepName = useClaimStore().claim?.claimLegalStep.name as ClaimLegalStepName;
    if (a.disabledSteps?.includes(claimStepName)) return false;
    if (a.legalSteps) {
      return a.legalSteps.includes(claimStepName);
    }
    return true;
  });
});

const modalOpen = ref(false);
const updateClaimModal = ref(false);
const modalCreateTask = ref(false);
</script>

<template>
  <CreateClaimTask :open="modalCreateTask" @close="modalCreateTask = false" :claim-id="useClaimStore().claim.id" />
  <AlertModal type="warning"
    title="Envoyer la mise en demeure"
    message="Êtes-vous sûr d'envoyer la mise en demeure ? Cela confiera également la gestion du dossier à Redlink. Cette action ne peut pas être annulée."
    confirm-message="Envoyer la MED" 
    :open=modalOpen @close="modalOpen = false" @confirm="modalOpen = false" @open="modalOpen = true"
  />
  <SendEventModal
    :open="sendEvent.open"
    :event="sendEvent.event"
    @cancel="sendEvent.open = false"
    @close="sendEvent.open = false"
    @confirm="sendEvent.open = false"
  />
  <UpdateClaimModal :open="updateClaimModal" @close="updateClaimModal = false" />
  <Disclosure as="nav" class="pl-2 pr-4 text-white bg-blue-900 border-b border-gray-200">
    <div class="flex justify-between h-16">
      <div class="flex items-center">
        <div class="items-center hidden sm:flex sm:space-x-2">

          <!-------------- Navigation -------------->
          <router-link :to="{ name: item.routeName }" v-for="item in navigation" :key="item.name"
            v-permission="item.permissions"
            active-class="text-white bg-blue-700 hover:bg-blue-600"
            class="relative px-3 py-2 text-sm font-medium text-gray-100 rounded-md hover:bg-blue-700 hover:text-white"
          >
            <div v-if="item.dot" class="absolute top-0 right-0 ">
              <!-- <div class="flex-none p-0.5 bg-red-400/30 animate-ping rounded-full">
                <div class="h-1.5 w-1.5 rounded-full bg-red-500"></div>
              </div> -->
              <span class="relative flex w-2 h-2">
                <span class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-sky-400"></span>
                <span class="relative inline-flex w-2 h-2 rounded-full bg-sky-500"></span>
              </span>
            </div>
            <!-- <span v-if="item.dot" class="absolute animate-bounce top-0 right-0 w-3 h-3 text-[0.5rem] bg-red-700 rounded-full flex items-center justify-center">1</span> -->
            {{ item.name }}
          </router-link>


          <!-------------- Actions -------------->
          <Menu as="div" class="relative inline-block text-left">
            <div>
              <MenuButton class="inline-flex justify-center w-full px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500">
                Actions
                <ChevronDownIcon class="w-5 h-5 ml-2 -mr-1" aria-hidden="true" />
              </MenuButton>
            </div>

            <!-- <transition
              enter-active-class="transition duration-100 ease-out transform"
              enter-from-class="scale-95 -translate-y-2"
              enter-to-class="translate-y-0 opacity-100"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="scale-100"
              leave-to-class="scale-95 -translate-y-2"
            > -->
            <transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
              <MenuItems class="absolute right-0 z-30 w-56 mt-2 origin-top-right bg-white rounded-md shadow-lg cursor-pointer ring-1 ring-black ring-opacity-5 focus:outline-none">
                <!-- @TODO Better border fix -->
                <div v-permission='["CUSTOMER_AGENT", "CUSTOMER_ADMIN", "BUSINESS_TRAVEL_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"]' class="mt-1"></div>
                <div v-permission="['UPCLAIM_ADMIN', 'LAWYER']" class="px-4 py-3 text-gray-800 border-b-2 border-gray-100 cursor-default" role="none">
                  <!-- Actions suivantes -->
                  <!-- Choisir étape suivante -->
                  <p v-if="machineStateActions.length" class="text-sm font-medium text-gray-900 truncate" role="none">Choisir la prochaine étape</p>
                  <p v-else class="text-sm italic font-medium text-gray-900">Dossier terminé</p>
                </div>
                <div v-for="item in stateActions" :key="item.name">
                  <div v-permission="item.permissions || ['*']" v-step="item.legalSteps">
                    <MenuItem v-slot="{ active }" class="py-1 border-b">
                    <a @click="item.click" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']">
                      <HeroIcons :name="item.heroIcon" :solid="false" class="w-6 h-6 mr-3 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                      {{ item.name }}
                    </a>
                  </MenuItem>
                  </div>
                </div>

                <div class="py-1" v-permission="['UPCLAIM_ADMIN', 'LAWYER']" v-for="item in machineStateActions" :key="item.name" >
                  <MenuItem v-slot="{ active }">
                    <a @click="item.click" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']">
                      <HeroIcons :name="item.heroIcon" :solid="true" class="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                      {{ item.name }}
                    </a>
                  </MenuItem>
                </div>

                <div class="border-t border-gray-200"></div>
                <div v-for="item in actions" :key="item.name">
                  <div v-permission="item.permissions || '*'" class="py-1">
                    <MenuItem v-slot="{ active }">
                      <a @click="item.click" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2 text-sm']">
                        <HeroIcons :name="item.heroIcon" :solid="false" class="w-5 h-5 mr-3 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                        {{ item.name }}
                      </a>
                    </MenuItem>
                  </div>
                </div>
              </MenuItems>
            </transition>
          </Menu>

        </div>
      </div>

      <!-------------- Mobile -------------->
      <div class="flex items-center flex-shrink-0">
        <img class="block w-auto h-8" src="/logo.svg" alt="Logo Upclaim" />
      </div>
      <div class="flex items-center sm:hidden">
        <!-- Mobile menu button -->
        <DisclosureButton class="inline-flex items-center justify-center p-2 mr-2 text-gray-400 bg-white rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="block w-6 h-6" aria-hidden="true" />
        </DisclosureButton>
      </div>
    </div>

    <DisclosurePanel class="sm:hidden">
      <div class="pt-2 pb-3 space-y-1">
        <a v-for="item in navigation" :key="item.name" :class="[item.current ? 'bg-blue-600 border-blue-500 text-white' : 'border-transparent text-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800', 'block pl-3 pr-4 py-2 border-l-4 text-base font-medium']" :aria-current="item.current ? 'page' : undefined">
          {{ item.name }}
        </a>
      </div>
      <div class="pt-4 pb-3 border-t border-gray-200" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
        <div class="mt-3 space-y-1">
          <a v-for="item in actions" :key="item.name" class="block px-4 py-2 text-base font-medium text-gray-200 hover:text-gray-800 hover:bg-gray-100">
            {{ item.name }}
          </a>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>
